import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import "../assets/css/catalog-products-list.css";
import NotificationComponent from "../components/Alert/alert";
import CatalogBarcodeScanPanel from "../components/Catalog/CatalogBarcodeScanPanel";
import CatalogReceivingModePanel from "../components/Catalog/CatalogReceivingModePanel";
import { printCenterReceiptPath } from "./printCenter/printCenterLinks";
import CatalogBarcodePrintModal from "../components/Print/CatalogBarcodePrintModal";
import {
  buildCatalogPrintItemFromScannedUnit,
  buildCatalogPrintItemFromVariantSummary,
  buildCatalogPrintItemsFromWorkspace,
  mergeCatalogPrintItems,
} from "../components/Print/catalogBarcodePrintItems";
import {
  useExecuteCatalogMigrationMutation,
  useFetchCatalogMigrationAuditQuery,
  useFetchCatalogProductsQuery,
  useFetchRecentCatalogVariantsQuery,
  useLazyFetchCatalogProductWorkspaceQuery,
  useLazyResolveCatalogBarcodeQuery,
  useReceiveCatalogBarcodeScanMutation,
  useUndoCatalogReceivingScanMutation,
} from "../api/api";
import {
  useFetchLocationsQuery,
  useFetchPanelTableQuery,
} from "../api/cmsPanelsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import { useTranslation } from "../hooks/useTranslation";
import {
  DEFAULT_CATALOG_LIST_FILTERS,
  buildCatalogListSearchParams,
  readCatalogListParams,
} from "../utils/catalogProductListParams";

const BUCKET_LABEL_KEYS = {
  serialized_variant_candidate: "catalogProducts.bucketSerialized",
  aggregate_simple_candidate: "catalogProducts.bucketAggregate",
  already_imported: "catalogProducts.bucketImported",
};

const formatDate = (value) => {
  if (!value) return "\u2014";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

const BULK_PRINT_PRODUCT_LIMIT = 25;

const PRODUCT_FAMILY_FILTER_OPTIONS = [
  { value: "shoes", labelKey: "catalogProductStudio.familyShoes" },
  { value: "textile", labelKey: "catalogProductStudio.familyTextile" },
  { value: "accessories", labelKey: "catalogProductStudio.familyAccessories" },
];

const getMigrationBucketColor = (bucket) => {
  if (bucket === "serialized_variant_candidate") return "info";
  if (bucket === "aggregate_simple_candidate") return "warning";
  if (bucket === "already_imported") return "dark";
  return "danger";
};

function CatalogProductsList() {
  const t = useTranslation();
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const listQuery = searchParams.toString();
  const { filters, page } = useMemo(
    () => readCatalogListParams(new URLSearchParams(listQuery)),
    [listQuery]
  );

  const sortOptions = useMemo(
    () => [
      { value: "operational", label: t("catalogProducts.sortOperational") },
      { value: "updated_desc", label: t("catalogProducts.sortRecentlyUpdated") },
      { value: "created_desc", label: t("catalogProducts.sortNewest") },
      { value: "published_desc", label: t("catalogProducts.sortRecentlyPublished") },
      { value: "title_asc", label: t("catalogProducts.sortTitleAz") },
    ],
    [t],
  );

  const statusMap = useMemo(
    () => ({
      active: { label: t("catalogProducts.statusPublished"), color: "success" },
      unpublished: { label: t("catalogProducts.statusPos"), color: "info" },
      draft: { label: t("catalogProducts.statusDraft"), color: "secondary" },
      archived: { label: t("catalogProducts.statusArchived"), color: "dark" },
    }),
    [t],
  );

  const stockRiskMap = useMemo(
    () => ({
      OUT_OF_STOCK: { label: t("catalogProducts.stockOut"), color: "danger" },
      LOW_STOCK: { label: t("catalogProducts.stockLow"), color: "warning" },
      OK: { label: t("catalogProducts.stockOk"), color: "success" },
    }),
    [t],
  );

  const getStatusBadge = (item) => {
    if (item?.status === "active" && !item?.publishing?.publishedAt) {
      return statusMap.unpublished;
    }
    return statusMap[item?.status] || statusMap.draft;
  };

  const getStockRiskBadge = (level) => stockRiskMap[level] || stockRiskMap.OK;

  const formatBucketLabel = (bucket) => {
    const key = BUCKET_LABEL_KEYS[bucket];
    if (key) return t(key);
    return String(bucket || "")
      .split("_")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  };

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [importingGroupKey, setImportingGroupKey] = useState(null);
  const [showMigration, setShowMigration] = useState(false);
  const [isQuickPrintOpen, setIsQuickPrintOpen] = useState(false);
  const [isScanPanelOpen, setIsScanPanelOpen] = useState(false);
  const [isReceivingModeOpen, setIsReceivingModeOpen] = useState(false);
  const [scanResolution, setScanResolution] = useState(null);
  const [printSidebarItems, setPrintSidebarItems] = useState([]);
  const [printSidebarCopiesById, setPrintSidebarCopiesById] = useState(null);
  const [printSidebarWarning, setPrintSidebarWarning] = useState("");
  const [printSidebarMode, setPrintSidebarMode] = useState("recent");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isBulkPrintLoading, setIsBulkPrintLoading] = useState(false);

  const updateListParams = useCallback(
    (nextFilters, nextPage, { replace = false } = {}) => {
      const nextParams = buildCatalogListSearchParams(nextFilters, nextPage);
      if (nextParams.toString() === searchParams.toString()) {
        return;
      }
      setSearchParams(nextParams, { replace });
    },
    [searchParams, setSearchParams]
  );

  const {
    data: catalogList,
    isFetching: isFetchingCatalog,
    isError: isCatalogError,
    error: catalogError,
    refetch: refetchCatalogList,
  } = useFetchCatalogProductsQuery({
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.brandId ? { brandId: Number(filters.brandId) } : {}),
    ...(filters.merchandisingTypeId
      ? { merchandisingTypeId: Number(filters.merchandisingTypeId) }
      : {}),
    ...(filters.productFamily ? { productFamily: filters.productFamily } : {}),
    ...(filters.stockRiskLevel ? { stockRiskLevel: filters.stockRiskLevel } : {}),
    ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
    page,
    pageSize: 24,
  });

  useEffect(() => {
    const totalPages = Number(catalogList?.totalPages);
    if (
      isFetchingCatalog ||
      !Number.isInteger(totalPages) ||
      totalPages < 1 ||
      page <= totalPages
    ) {
      return;
    }

    const nextParams = buildCatalogListSearchParams(filters, totalPages);
    if (nextParams.toString() === searchParams.toString()) {
      return;
    }
    setSearchParams(nextParams, { replace: true });
  }, [
    catalogList?.totalPages,
    filters,
    isFetchingCatalog,
    page,
    searchParams,
    setSearchParams,
  ]);

  const { data: brandOptions = [] } = useFetchPanelTableQuery("brands");
  const { data: productTypeOptions = [] } = useFetchPanelTableQuery("types");

  const {
    data: migrationAudit,
    isFetching: isFetchingAudit,
    isError: isAuditError,
    error: auditError,
    refetch: refetchMigrationAudit,
  } = useFetchCatalogMigrationAuditQuery();

  const [executeCatalogMigration, { isLoading: isExecutingMigration }] =
    useExecuteCatalogMigrationMutation();
  const [resolveCatalogBarcode, { isFetching: isResolvingBarcode }] =
    useLazyResolveCatalogBarcodeQuery();
  const [receiveCatalogBarcodeScan, { isLoading: isProcessingReceivingScan }] =
    useReceiveCatalogBarcodeScanMutation();
  const [undoCatalogReceivingScan, { isLoading: isUndoingReceivingScan }] =
    useUndoCatalogReceivingScanMutation();
  const [fetchCatalogProductWorkspace] = useLazyFetchCatalogProductWorkspaceQuery();
  const { data: locations = [] } = useFetchLocationsQuery();

  const {
    data: recentVariantsData,
    isFetching: isFetchingRecentVariants,
  } = useFetchRecentCatalogVariantsQuery(
    { limit: 40 },
    { skip: !isQuickPrintOpen || printSidebarMode !== "recent" }
  );

  const handleFilterChange = ({ target: { name, value } }) => {
    setSelectedProductIds([]);
    updateListParams(
      { ...filters, [name]: value },
      1,
      { replace: name === "search" }
    );
  };

  const resetFilters = () => {
    setSelectedProductIds([]);
    updateListParams({ ...DEFAULT_CATALOG_LIST_FILTERS }, 1);
  };

  const enterSelectionMode = () => {
    setIsSelectionMode(true);
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedProductIds([]);
  };

  const handleImportGroup = async (groupKey) => {
    setImportingGroupKey(groupKey);
    try {
      const result = await executeCatalogMigration({
        groupKeys: [groupKey],
      }).unwrap();
      const createdResult = (result?.results || []).find(
        (entry) => entry.status === "created"
      );
      await Promise.all([refetchCatalogList(), refetchMigrationAudit()]);
      if (createdResult?.catalogProductId) {
        notificationRef.current?.showNotification(
          t("catalogProducts.importSuccess"),
          "success"
        );
        navigate(`/admin/catalog/products/${createdResult.catalogProductId}`);
        return;
      }
      notificationRef.current?.showNotification(
        t("catalogProducts.importNoRecord"),
        "warning"
      );
    } catch (error) {
      notificationRef.current?.showNotification(
        getApiErrorMessage(error, t("catalogProducts.importError")),
        "danger"
      );
    } finally {
      setImportingGroupKey(null);
    }
  };

  const listItems = useMemo(() => catalogList?.items || [], [catalogList?.items]);
  const listSummary = catalogList?.summary || {
    byStatus: {},
    bySourceType: {},
    byTransitionState: {},
    totalVariants: 0,
    totalAvailableToSell: 0,
  };

  const migrationGroups = migrationAudit?.groups || [];
  const prioritizedMigrationGroups = [...migrationGroups]
    .sort((a, b) => {
      if (a.executionSupported === b.executionSupported)
        return a.title.localeCompare(b.title);
      return a.executionSupported ? -1 : 1;
    })
    .slice(0, 6);

  const hasMigrationGroups = prioritizedMigrationGroups.length > 0;
  const activeFilterCount = [
    filters.search.trim(),
    filters.status,
    filters.brandId,
    filters.merchandisingTypeId,
    filters.productFamily,
    filters.stockRiskLevel,
  ].filter(Boolean).length;
  const recentVariantItems = useMemo(
    () => (recentVariantsData?.items || []).map(buildCatalogPrintItemFromVariantSummary),
    [recentVariantsData]
  );
  const visibleProductIds = useMemo(
    () => listItems.map((item) => item.id),
    [listItems]
  );
  const selectedVisibleCount = useMemo(
    () => visibleProductIds.filter((id) => selectedProductIds.includes(id)).length,
    [selectedProductIds, visibleProductIds]
  );
  const allVisibleSelected =
    visibleProductIds.length > 0 &&
    selectedVisibleCount === visibleProductIds.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && !allVisibleSelected;

  const resetPrintSidebarState = useCallback(() => {
    setPrintSidebarItems([]);
    setPrintSidebarCopiesById(null);
    setPrintSidebarWarning("");
    setPrintSidebarMode("recent");
    setIsBulkPrintLoading(false);
  }, []);

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const toggleVisibleProductSelection = () => {
    setSelectedProductIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleProductIds.includes(id));
      }

      const next = new Set(current);
      visibleProductIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearProductSelection = () => {
    setSelectedProductIds([]);
  };

  const handlePageChange = (nextPage) => {
    updateListParams(filters, nextPage);
  };

  const getProductTitleById = (productId) =>
    listItems.find((item) => item.id === productId)?.title || productId;

  const handleBulkPrintSelectedProducts = async () => {
    const productIds = [...selectedProductIds];

    if (!productIds.length) {
      return;
    }

    if (productIds.length > BULK_PRINT_PRODUCT_LIMIT) {
      notificationRef.current?.showNotification(
        t("catalogProducts.bulkPrintLimit").replace(
          "{limit}",
          String(BULK_PRINT_PRODUCT_LIMIT)
        ),
        "warning"
      );
      return;
    }

    resetPrintSidebarState();
    setPrintSidebarMode("bulk");
    setIsBulkPrintLoading(true);
    setIsQuickPrintOpen(true);

    const warnings = [];
    const failedProducts = [];
    const emptyProducts = [];
    const loadedItems = [];

    // Items that cannot be printed (no barcode, serialized without unit
    // labels) are kept and explained by the print panel, never dropped here.
    await Promise.all(
      productIds.map(async (productId) => {
        try {
          const workspace = await fetchCatalogProductWorkspace(productId).unwrap();
          const productItems = buildCatalogPrintItemsFromWorkspace(workspace);

          if (!productItems.length) {
            emptyProducts.push(getProductTitleById(productId));
            return;
          }

          loadedItems.push(...productItems);
        } catch (_error) {
          failedProducts.push(getProductTitleById(productId));
        }
      })
    );

    const mergedItems = mergeCatalogPrintItems(loadedItems);
    const hasPrintableItems = mergedItems.some((item) => !item.blockedReason);

    if (failedProducts.length) {
      warnings.push(
        `${t("catalogProducts.bulkPrintLoadFailed")}: ${failedProducts.join(", ")}`
      );
    }

    if (emptyProducts.length) {
      warnings.push(
        `${t("catalogProducts.bulkPrintNoVariants")}: ${emptyProducts.join(", ")}`
      );
    }

    if (!hasPrintableItems) {
      warnings.push(t("catalogProducts.bulkPrintNothingPrintable"));
    }

    setPrintSidebarItems(mergedItems);
    setPrintSidebarWarning(warnings.join(" "));
    setIsBulkPrintLoading(false);

    if (!hasPrintableItems) {
      notificationRef.current?.showNotification(
        t("catalogProducts.bulkPrintNothingPrintable"),
        "warning"
      );
    }
  };

  const handleResolveBarcode = async (barcode) => {
    try {
      const result = await resolveCatalogBarcode({ barcode }).unwrap();
      setScanResolution(result);
    } catch (error) {
      notificationRef.current?.showNotification(
        getApiErrorMessage(error, t("catalogProducts.barcodeResolveError")),
        "danger"
      );
    }
  };

  const handleOpenProductFromScan = (resolution) => {
    if (!resolution?.product?.id) {
      return;
    }

    setIsScanPanelOpen(false);
    navigate(`/admin/catalog/products/${resolution.product.id}`);
  };

  const handleRestockFromScan = (resolution) => {
    if (!resolution?.product?.id || !resolution?.variant?.id) {
      return;
    }

    setIsScanPanelOpen(false);
    navigate(`/admin/catalog/products/${resolution.product.id}#inventory`, {
      state: {
        catalogAction: {
          type: "restock",
          variantId: resolution.variant.id,
        },
      },
    });
  };

  const handleAdjustFromScan = (resolution) => {
    if (!resolution?.product?.id || !resolution?.variant?.id) {
      return;
    }

    setIsScanPanelOpen(false);
    navigate(`/admin/catalog/products/${resolution.product.id}#inventory`, {
      state: {
        catalogAction: {
          type: "adjust",
          variantId: resolution.variant.id,
        },
      },
    });
  };

  const handlePrintFromScan = (resolution) => {
    const isSerialized =
      String(resolution?.product?.inventoryMode || "").toLowerCase() === "serialized";
    // A serialized item sells by its own unit label, never the variant barcode.
    const item = resolution?.inventoryUnit
      ? buildCatalogPrintItemFromScannedUnit(resolution)
      : buildCatalogPrintItemFromVariantSummary({
          variantId: resolution?.variant?.id,
          productId: resolution?.product?.id,
          productTitle: resolution?.product?.title,
          brandName: resolution?.product?.brandName,
          inventoryMode: resolution?.product?.inventoryMode,
          variantTitle: resolution?.variant?.title,
          sku: resolution?.variant?.sku,
          barcode: resolution?.variant?.barcode,
          price: resolution?.variant?.price,
          compareAtPrice: resolution?.variant?.compareAtPrice,
          inventory: resolution?.variant?.inventory,
        });

    if (item.blockedReason) {
      notificationRef.current?.showNotification(
        t(
          isSerialized && !resolution?.inventoryUnit
            ? "catalogProducts.serializedScanNeedsUnit"
            : "catalogProducts.noPrintableBarcode"
        ),
        "warning"
      );
      return;
    }

    setIsScanPanelOpen(false);
    resetPrintSidebarState();
    setPrintSidebarMode("preset");
    setPrintSidebarItems([item]);
    setPrintSidebarCopiesById(null);
    setIsQuickPrintOpen(true);
  };

  const handleProcessReceivingScan = async (payload) => {
    try {
      return await receiveCatalogBarcodeScan(payload).unwrap();
    } catch (error) {
      notificationRef.current?.showNotification(
        getApiErrorMessage(error, t("catalogProducts.scanProcessError")),
        "danger"
      );
      return null;
    }
  };

  const handleUndoReceivingScan = async (payload) => {
    try {
      return await undoCatalogReceivingScan(payload).unwrap();
    } catch (error) {
      notificationRef.current?.showNotification(
        getApiErrorMessage(error, t("catalogProducts.undoScanError")),
        "danger"
      );
      return null;
    }
  };

  return (
    <div className="content catalog-products-list">
      <NotificationComponent ref={notificationRef} />

      <Row>
        <Col lg="12">
          <Card className="catalog-ops-hero-card">
            <CardBody>
              <div className="catalog-ops-hero">
                <div className="catalog-ops-hero-copy">
                  <h3 className="catalog-ops-title">{t("catalogProducts.title")}</h3>
                  <p className="catalog-ops-copy">
                    {t("catalogProducts.subtitle")}
                  </p>
                </div>
                <div className="catalog-ops-hero-actions">
                  <Button
                    color="outline-light"
                    onClick={() => setIsReceivingModeOpen(true)}
                  >
                    {t("catalogProducts.receivingMode")}
                  </Button>
                  <Button
                    color="outline-light"
                    onClick={() => {
                      setScanResolution(null);
                      setIsScanPanelOpen(true);
                    }}
                  >
                    {t("catalogProducts.scanBarcode")}
                  </Button>
                  <Button
                    color="outline-light"
                    onClick={() => {
                      resetPrintSidebarState();
                      setIsQuickPrintOpen(true);
                    }}
                  >
                    {t("catalogProducts.quickPrint")}
                  </Button>
                  <Link
                    to="/admin/catalog/products/new"
                    className="btn btn-primary"
                  >
                    {t("catalogProducts.addProduct")}
                  </Link>
                </div>
              </div>

              <div className="catalog-ops-summary-strip">
                <div className="catalog-ops-summary-item">
                  <span>{t("catalogProducts.draft")}</span>
                  <strong>{listSummary.byStatus?.draft || 0}</strong>
                </div>
                <div className="catalog-ops-summary-item">
                  <span>{t("catalogProducts.published")}</span>
                  <strong>{listSummary.byStatus?.active || 0}</strong>
                </div>
                <div className="catalog-ops-summary-item">
                  <span>{t("catalogProducts.archived")}</span>
                  <strong>{listSummary.byStatus?.archived || 0}</strong>
                </div>
                <div className="catalog-ops-summary-item">
                  <span>{t("catalogProducts.inStock")}</span>
                  <strong>{listSummary.totalAvailableToSell || 0}</strong>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={hasMigrationGroups && showMigration ? "8" : "12"}>
          <Card>
            <CardHeader className="catalog-ops-list-header">
              <div>
                <CardTitle tag="h3">{t("catalogProducts.allProducts")}</CardTitle>
                <p className="catalog-ops-section-copy">
                  {t("catalogProducts.productsCount").replace(
                    "{count}",
                    String(catalogList?.total || listItems.length),
                  )}
                  {activeFilterCount > 0 ? ` ${t("catalogProducts.matchingFilters")}` : ""}
                </p>
              </div>
              <div className="catalog-ops-list-meta">
                <Button
                  color={isSelectionMode ? "secondary" : "outline-light"}
                  size="sm"
                  onClick={isSelectionMode ? exitSelectionMode : enterSelectionMode}
                  aria-pressed={isSelectionMode}
                >
                  {isSelectionMode
                    ? t("catalogProducts.cancelSelection")
                    : t("catalogProducts.selectProducts")}
                </Button>
                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    className="catalog-ops-clear-button"
                    onClick={resetFilters}
                  >
                    {t("catalogProducts.clearFilters")}
                  </button>
                ) : null}
                {hasMigrationGroups ? (
                  <button
                    type="button"
                    className="catalog-ops-migration-toggle"
                    onClick={() => setShowMigration((c) => !c)}
                  >
                    {showMigration
                      ? t("catalogProducts.hideImports")
                      : t("catalogProducts.importFromLegacy")}
                  </button>
                ) : null}
              </div>
            </CardHeader>
            <CardBody>
              {isSelectionMode ? (
                <div
                  className="catalog-ops-selection-toolbar"
                  role="toolbar"
                  aria-label={t("catalogProducts.selectionToolbarAria")}
                >
                  <span className="catalog-ops-selection-count">
                    {t("catalogProducts.selectedProductsCount").replace(
                      "{count}",
                      String(selectedProductIds.length)
                    )}
                  </span>
                  <div className="catalog-ops-selection-actions">
                    <Button
                      color="link"
                      size="sm"
                      className="catalog-ops-selection-link"
                      onClick={toggleVisibleProductSelection}
                      disabled={!visibleProductIds.length}
                      aria-label={t("catalogProducts.selectAllVisible")}
                    >
                      {t("catalogProducts.selectAllVisible")}
                    </Button>
                    <Button
                      color="link"
                      size="sm"
                      className="catalog-ops-selection-link"
                      onClick={clearProductSelection}
                      disabled={!selectedProductIds.length}
                      aria-label={t("catalogProducts.clearSelection")}
                    >
                      {t("catalogProducts.clearSelection")}
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      disabled={!selectedProductIds.length || isBulkPrintLoading}
                      aria-label={t("catalogProducts.printAllVariants")}
                      onClick={handleBulkPrintSelectedProducts}
                    >
                      {isBulkPrintLoading
                        ? t("catalogProducts.bulkPrintLoading")
                        : t("catalogProducts.printAllVariants")}
                    </Button>
                    <Button
                      color="link"
                      size="sm"
                      className="catalog-ops-selection-link"
                      onClick={exitSelectionMode}
                    >
                      {t("catalogProducts.cancelSelection")}
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="catalog-ops-filter-bar">
                <div className="catalog-ops-search-wrap">
                  <Input
                    id="catalog-search"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder={t("catalogProducts.searchByName")}
                  />
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-brand"
                    type="select"
                    name="brandId"
                    value={filters.brandId}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterBrand")}
                  >
                    <option value="">{t("catalogProducts.allBrands")}</option>
                    {brandOptions.map((brand) => (
                      <option key={brand.id} value={String(brand.id)}>
                        {brand.name || brand.brandName}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-product-type"
                    type="select"
                    name="merchandisingTypeId"
                    value={filters.merchandisingTypeId}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterProductType")}
                  >
                    <option value="">{t("catalogProducts.allProductTypes")}</option>
                    {productTypeOptions.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.name || type.specificType}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-product-family"
                    type="select"
                    name="productFamily"
                    value={filters.productFamily}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterCategory")}
                  >
                    <option value="">{t("catalogProducts.allCategories")}</option>
                    {PRODUCT_FAMILY_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-status"
                    type="select"
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterStatus")}
                  >
                    <option value="">{t("catalogProducts.allStatuses")}</option>
                    <option value="active">{t("catalogProducts.statusPublished")}</option>
                    <option value="unpublished">{t("catalogProducts.statusPos")}</option>
                    <option value="draft">{t("catalogProducts.statusDraft")}</option>
                    <option value="archived">{t("catalogProducts.statusArchived")}</option>
                  </Input>
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-stock"
                    type="select"
                    name="stockRiskLevel"
                    value={filters.stockRiskLevel}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterStock")}
                  >
                    <option value="">{t("catalogProducts.allStockStates")}</option>
                    <option value="OK">{t("catalogProducts.stockOk")}</option>
                    <option value="LOW_STOCK">{t("catalogProducts.stockLow")}</option>
                    <option value="OUT_OF_STOCK">{t("catalogProducts.stockOut")}</option>
                  </Input>
                </div>
                <div className="catalog-ops-select-wrap">
                  <Input
                    id="catalog-sort"
                    type="select"
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    aria-label={t("catalogProducts.filterSort")}
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>

              {isCatalogError ? (
                <Alert color="danger" className="mb-0 mt-3">
                  {getApiErrorMessage(
                    catalogError,
                    t("catalogProducts.loadError")
                  )}
                </Alert>
              ) : isFetchingCatalog ? (
                <div className="catalog-ops-loading">
                  <Spinner color="primary" />
                </div>
              ) : listItems.length ? (
                <>
                <div className="catalog-ops-list-surface catalog-ops-section-gap">
                  <Table
                    responsive
                    className={`tablesorter catalog-ops-table${
                      isSelectionMode ? " catalog-ops-table--selection" : ""
                    }`}
                  >
                    <thead className="text-primary">
                      <tr>
                        {isSelectionMode ? (
                          <th className="catalog-ops-col-select" scope="col">
                            <label className="catalog-ops-select-control">
                              <input
                                type="checkbox"
                                className="catalog-ops-select-input"
                                checked={allVisibleSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = someVisibleSelected;
                                  }
                                }}
                                aria-label={t("catalogProducts.selectAllVisible")}
                                onChange={toggleVisibleProductSelection}
                                onClick={(event) => event.stopPropagation()}
                              />
                            </label>
                          </th>
                        ) : null}
                        <th className="catalog-ops-col-product" scope="col">
                          {t("catalogProducts.product")}
                        </th>
                        <th className="catalog-ops-col-stock" scope="col">
                          {t("catalogProducts.stock")}
                        </th>
                        <th className="catalog-ops-col-variants" scope="col">
                          {t("catalogProducts.variants")}
                        </th>
                        <th className="catalog-ops-col-updated" scope="col">
                          {t("catalogProducts.updated")}
                        </th>
                        <th className="catalog-ops-col-action text-right" scope="col">
                          {t("catalogProducts.action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {listItems.map((item) => {
                        const statusInfo = getStatusBadge(item);
                        const isProductSelected = selectedProductIds.includes(item.id);
                        const hasMedia =
                          typeof item.hasMedia === "boolean" ? item.hasMedia : null;
                        const isInMetaCatalog =
                          typeof item.metaCatalogEnabled === "boolean"
                            ? item.metaCatalogEnabled
                            : null;
                        return (
                          <tr
                            key={item.id}
                            className={`catalog-ops-row${
                              isProductSelected ? " catalog-ops-row--selected" : ""
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              if (
                                e.target.closest(
                                  "button, a, input, label, .catalog-ops-col-select"
                                )
                              ) {
                                return;
                              }
                              navigate(`/admin/catalog/products/${item.id}`);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/admin/catalog/products/${item.id}`);
                              }
                            }}
                          >
                            {isSelectionMode ? (
                              <td className="catalog-ops-col-select">
                                <label className="catalog-ops-select-control">
                                  <input
                                    type="checkbox"
                                    className="catalog-ops-select-input"
                                    checked={isProductSelected}
                                    aria-label={t("catalogProducts.selectProduct").replace(
                                      "{title}",
                                      item.title
                                    )}
                                    onChange={() => toggleProductSelection(item.id)}
                                    onClick={(event) => event.stopPropagation()}
                                  />
                                </label>
                              </td>
                            ) : null}
                            <td className="catalog-ops-col-product">
                              <span className="catalog-ops-row-title">
                                {item.title}
                              </span>
                              <div className="catalog-ops-row-meta">
                                {item.productFamily ? (
                                  <span>{item.productFamily}</span>
                                ) : null}
                                {item.brandName ? (
                                  <span>{item.brandName}</span>
                                ) : null}
                                {item.merchandisingTypeName ? (
                                  <span>{item.merchandisingTypeName}</span>
                                ) : null}
                              </div>
                              <div className="catalog-ops-badge-row">
                                <Badge color={statusInfo.color} pill>
                                  {statusInfo.label}
                                </Badge>
                                {item.sourceType === "legacy_imported" ? (
                                  <Badge color="warning" pill>
                                    {t("catalogProducts.imported")}
                                  </Badge>
                                ) : null}
                                <Badge
                                  color={getStockRiskBadge(item.inventory?.stockRiskLevel).color}
                                  pill
                                >
                                  {getStockRiskBadge(item.inventory?.stockRiskLevel).label}
                                </Badge>
                                {hasMedia === null ? null : (
                                  <Badge color={hasMedia ? "success" : "warning"} pill>
                                    {hasMedia
                                      ? t("catalogProducts.hasPhotos")
                                      : t("catalogProducts.noPhotos")}
                                  </Badge>
                                )}
                                {isInMetaCatalog === null ? null : (
                                  <Badge
                                    color={isInMetaCatalog ? "info" : "secondary"}
                                    pill
                                  >
                                    {isInMetaCatalog
                                      ? t("catalogProducts.inMetaCatalog")
                                      : t("catalogProducts.notInMetaCatalog")}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="catalog-ops-col-stock">
                              <div className="catalog-ops-stat">
                                <strong>
                                  {item.inventory.availableToSell}
                                </strong>
                                <span>{t("catalogProducts.available")}</span>
                              </div>
                            </td>
                            <td className="catalog-ops-col-variants">
                              <div className="catalog-ops-stat">
                                <strong>{item.liveVariantCount}</strong>
                                <span>
                                  {item.liveVariantCount === 1
                                    ? t("catalogProducts.variant")
                                    : t("catalogProducts.variantsPlural")}
                                </span>
                              </div>
                            </td>
                            <td className="catalog-ops-col-updated">
                              <span className="catalog-ops-muted">
                                {formatDate(item.updatedAt)}
                              </span>
                            </td>
                            <td className="catalog-ops-col-action text-right">
                              <div className="catalog-ops-action-stack">
                                <Button
                                  color="link"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/admin/catalog/products/${item.id}`
                                    );
                                  }}
                                >
                                  {t("catalogProducts.open")}
                                </Button>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/admin/catalog/products/${item.id}#inventory`
                                    );
                                  }}
                                >
                                  {t("catalogProducts.restock")}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                {catalogList?.totalPages > 1 ? (
                  <div className="catalog-ops-pagination">
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={page <= 1 || isFetchingCatalog}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      {t("catalogProducts.previousPage")}
                    </Button>
                    <span className="catalog-ops-pagination-label">
                      {t("catalogProducts.pageOf")
                        .replace("{page}", String(page))
                        .replace("{totalPages}", String(catalogList.totalPages))}
                    </span>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={page >= catalogList.totalPages || isFetchingCatalog}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      {t("catalogProducts.nextPage")}
                    </Button>
                  </div>
                ) : null}
                </>
              ) : (
                <Alert color="secondary" className="mb-0 mt-3">
                  {t("catalogProducts.noProductsMatch")}
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>

        {hasMigrationGroups && showMigration ? (
          <Col lg="4">
            <Card className="catalog-ops-migration-card">
              <CardHeader>
                <CardTitle tag="h3">{t("catalogProducts.legacyImport")}</CardTitle>
                <p className="catalog-ops-section-copy">
                  {t("catalogProducts.legacyImportDesc")}
                </p>
              </CardHeader>
              <CardBody>
                {isAuditError ? (
                  <Alert color="danger" className="mb-0">
                    {getApiErrorMessage(
                      auditError,
                      t("catalogProducts.importLoadError")
                    )}
                  </Alert>
                ) : isFetchingAudit ? (
                  <div className="catalog-ops-loading">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <>
                    <div className="catalog-ops-audit-summary">
                      <div>
                        <span>{t("catalogProducts.ready")}</span>
                        <strong>
                          {migrationAudit?.summary?.executableCount || 0}
                        </strong>
                      </div>
                      <div>
                        <span>{t("catalogProducts.blocked")}</span>
                        <strong>
                          {migrationAudit?.summary?.blockedCount || 0}
                        </strong>
                      </div>
                      <div>
                        <span>{t("catalogProducts.done")}</span>
                        <strong>
                          {migrationAudit?.summary?.alreadyImportedCount || 0}
                        </strong>
                      </div>
                    </div>

                    <div className="catalog-ops-migration-list">
                      {prioritizedMigrationGroups.map((group) => (
                        <div
                          key={group.groupKey}
                          className="catalog-ops-migration-item"
                        >
                          <div className="catalog-ops-migration-topline">
                            <div>
                              <button
                                type="button"
                                className="catalog-ops-link-button"
                                onClick={() => {
                                  if (group.catalogProductId) {
                                    navigate(
                                      `/admin/catalog/products/${group.catalogProductId}`
                                    );
                                  }
                                }}
                              >
                                {group.title}
                              </button>
                              <div className="catalog-ops-row-meta">
                                {group.brandName ? (
                                  <span>{group.brandName}</span>
                                ) : null}
                                {group.merchandisingTypeName ? (
                                  <span>{group.merchandisingTypeName}</span>
                                ) : null}
                              </div>
                            </div>
                            <Badge
                              color={getMigrationBucketColor(group.bucket)}
                              pill
                            >
                              {formatBucketLabel(group.bucket)}
                            </Badge>
                          </div>
                          {group.blockers?.length ? (
                            <p className="catalog-ops-migration-note catalog-ops-migration-note-danger">
                              {group.blockers[0]}
                            </p>
                          ) : (
                            <p className="catalog-ops-migration-note">
                              {t("catalogProducts.readyToImport")}
                            </p>
                          )}
                          <div className="catalog-ops-migration-actions">
                            {group.executionSupported ? (
                              <Button
                                color="secondary"
                                size="sm"
                                disabled={
                                  isExecutingMigration &&
                                  importingGroupKey === group.groupKey
                                }
                                onClick={() =>
                                  handleImportGroup(group.groupKey)
                                }
                              >
                                {isExecutingMigration &&
                                importingGroupKey === group.groupKey
                                  ? t("catalogProducts.importing")
                                  : t("catalogProducts.import")}
                              </Button>
                            ) : group.catalogProductId ? (
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/admin/catalog/products/${group.catalogProductId}`
                                  )
                                }
                              >
                                {t("catalogProducts.view")}
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        ) : null}
      </Row>

      <CatalogBarcodePrintModal
        isOpen={isQuickPrintOpen}
        toggle={() => {
          setIsQuickPrintOpen(false);
          resetPrintSidebarState();
        }}
        items={
          printSidebarMode === "recent"
            ? recentVariantItems
            : printSidebarItems
        }
        initialCopiesById={printSidebarCopiesById}
        selectable={printSidebarMode === "recent"}
        isLoading={
          printSidebarMode === "bulk"
            ? isBulkPrintLoading
            : printSidebarMode === "recent" && isFetchingRecentVariants
        }
        warningMessage={printSidebarWarning}
        loadingMessage={
          printSidebarMode === "bulk"
            ? t("catalogProducts.bulkPrintLoadingVariants")
            : t("catalogProducts.loadingRecentVariants")
        }
        emptyMessage={
          printSidebarMode === "bulk"
            ? t("catalogProducts.bulkPrintNothingPrintable")
            : t("catalogProducts.noRecentPrintableVariants")
        }
        title={
          printSidebarMode === "bulk"
            ? t("catalogProducts.bulkPrintTitle")
            : printSidebarMode === "preset"
            ? t("catalogProducts.printBarcodeLabels")
            : t("catalogProducts.quickPrintRecent")
        }
        description={
          printSidebarMode === "bulk"
            ? t("catalogProducts.bulkPrintDescription")
            : printSidebarMode === "preset"
            ? t("catalogProducts.printResolvedVariant")
            : t("catalogProducts.quickPrintBatch")
        }
        onPrinted={() =>
          notificationRef.current?.showNotification(
            t("catalogProducts.labelsSentToPrint"),
            "success"
          )
        }
      />

      <CatalogBarcodeScanPanel
        isOpen={isScanPanelOpen}
        onClose={() => {
          setIsScanPanelOpen(false);
          setScanResolution(null);
        }}
        onResolve={handleResolveBarcode}
        resolution={scanResolution}
        isResolving={isResolvingBarcode}
        onPrint={handlePrintFromScan}
        onRestock={handleRestockFromScan}
        onAdjust={handleAdjustFromScan}
        onOpenProduct={handleOpenProductFromScan}
      />

      <CatalogReceivingModePanel
        isOpen={isReceivingModeOpen}
        onClose={() => setIsReceivingModeOpen(false)}
        locations={locations}
        defaultLocationId={locations?.[0]?.id}
        onProcessScan={handleProcessReceivingScan}
        onUndoScan={handleUndoReceivingScan}
        isProcessing={isProcessingReceivingScan}
        isUndoing={isUndoingReceivingScan}
        onOpenPrintCenter={(receiptIds) => {
          setIsReceivingModeOpen(false);
          navigate(receiptIds ? printCenterReceiptPath(receiptIds) : "/admin/print-center?view=needs-labels");
        }}
        onPrintSession={({ items, copiesByKey }) => {
          if (!items?.length) {
            notificationRef.current?.showNotification(
              t("catalogProducts.noPrintableVariants"),
              "warning"
            );
            return;
          }

          setIsReceivingModeOpen(false);
          resetPrintSidebarState();
          setPrintSidebarMode("preset");
          setPrintSidebarItems(items);
          setPrintSidebarCopiesById(copiesByKey);
          setIsQuickPrintOpen(true);
        }}
        onOpenProduct={(result) => {
          if (!result?.product?.id) {
            return;
          }

          setIsReceivingModeOpen(false);
          navigate(`/admin/catalog/products/${result.product.id}#inventory`);
        }}
      />
    </div>
  );
}

export default CatalogProductsList;
